import { IfAbility, SessionService } from '@efdigital/ll-addon-auth';
import {
  CohortNavTemplate,
  ProgramNavTemplate,
} from '@efdigital/ll-addon-content';
import MessagePost from '@efdigital/ll-addon-content/components/messages/message-post';
import PostActions from '@efdigital/ll-addon-content/components/messages/post-actions';
import PostEditor from '@efdigital/ll-addon-content/components/messages/post-editor';
import type { Message } from '@efdigital/ll-addon-data';
import { DataTypeXceed } from '@efdigital/ll-addon-data/data-types/xceed';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { programTemplateParts } from '@efdigital/ll-admin-v2/components/parts.gts';
import ContentBreadcrumb from '@efdigital/ll-admin-v2/components/programs/content-breadcrumb.gts';
import { EditCohortVisibility } from '@efdigital/ll-admin-v2/components/programs/edit-cohort-visibility.gts';
import { Sidebar } from '@efdigital/ll-admin-v2/components/sidebar/sidebar.gts';
import { setAsyncBelongsTo } from '@efdigital/ll-admin-v2/data/async-setter';
import { queryCohortMessages } from '@efdigital/ll-admin-v2/data/builders/xceed/messages';
import { deepSave } from '@efdigital/ll-admin-v2/data/deep-save';
import type { ProgramsProgramCohortContentDiscussionsModel } from '@efdigital/ll-admin-v2/routes/programs/program/cohort/content/discussions';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';
import { pageTitle } from 'ember-page-title';

interface Signature {
  Args: {
    model: ProgramsProgramCohortContentDiscussionsModel;
  };
}

const parts = programTemplateParts;

/**
 * Filter to get only top-level threads (no parent).
 */
function filterThreads(messages: Message[]): Message[] {
  return messages.filter((msg) => !msg.parent?.id);
}

/**
 * Get replies for a specific thread.
 */
function getRepliesForThread(
  messages: Message[],
  threadId: string | null
): Message[] {
  if (!threadId) {
    return [];
  }
  return messages.filter((msg) => msg.parent?.id === threadId);
}

function isPlural(count: number): boolean {
  return count !== 1;
}

export default class ProgramsProgramCohortContentDiscussionsIndexTemplate extends Component<Signature> {
  @service declare store: Store;
  @service declare session: SessionService;

  @tracked showNewThreadModal = false;
  @tracked showReplyModal = false;
  @tracked showEditModal = false;
  @tracked newThread: Message | null = null;
  @tracked newReply: Message | null = null;
  @tracked editingMessage: Message | null = null;
  @tracked replyingToThread: Message | null = null;
  @tracked expandedThreads = new Set<string>();
  @tracked queryVersion = 0;

  get query() {
    // Include queryVersion to force re-query when incremented
    void this.queryVersion;
    return queryCohortMessages(this.args.model.cohort_id, 'discussion');
  }

  reload = () => {
    this.queryVersion++;
  };

  // Thread expansion
  isExpanded = (threadId: string | null): boolean => {
    if (!threadId) {
      return false;
    }
    return this.expandedThreads.has(threadId);
  };

  toggleThread = (threadId: string | null) => {
    if (!threadId) {
      return;
    }
    const newSet = new Set(this.expandedThreads);
    if (newSet.has(threadId)) {
      newSet.delete(threadId);
    } else {
      newSet.add(threadId);
    }
    this.expandedThreads = newSet;
  };

  // New thread
  openNewThreadModal = () => {
    const thread = this.store.createRecord<Message>(DataTypeXceed.Message, {
      user: this.session.cached.userId!,
      markdown: '',
      category: 'discussion',
      startDate: new Date().toISOString(),
    });
    setAsyncBelongsTo(thread, 'cohort', this.args.model.cohort);
    this.newThread = thread;
    this.showNewThreadModal = true;
  };

  closeNewThreadModal = () => {
    if (this.newThread && !this.newThread.id) {
      this.newThread.unloadRecord();
    }
    this.newThread = null;
    this.showNewThreadModal = false;
  };

  saveThread = async (message: Message) => {
    await deepSave(message, this.store, { maxDepth: 1 });
    this.showNewThreadModal = false;
    this.reload();
  };

  // Reply to thread
  openReplyModal = (thread: Message) => {
    const reply = this.store.createRecord<Message>(DataTypeXceed.Message, {
      user: this.session.cached.userId!,
      markdown: '',
      category: 'discussion',
      startDate: new Date().toISOString(),
    });
    setAsyncBelongsTo(reply, 'cohort', this.args.model.cohort);
    setAsyncBelongsTo(reply, 'parent', thread);
    this.newReply = reply;
    this.replyingToThread = thread;
    this.showReplyModal = true;
  };

  closeReplyModal = () => {
    if (this.newReply && !this.newReply.id) {
      this.newReply.unloadRecord();
    }
    this.newReply = null;
    this.replyingToThread = null;
    this.showReplyModal = false;
  };

  saveReply = async (message: Message) => {
    await deepSave(message, this.store, { maxDepth: 1 });
    this.showReplyModal = false;
    // Expand the thread to show the new reply
    if (this.replyingToThread?.id) {
      const newSet = new Set(this.expandedThreads);
      newSet.add(this.replyingToThread.id);
      this.expandedThreads = newSet;
    }
    this.replyingToThread = null;
    this.reload();
  };

  // Edit message
  openEditModal = (message: Message) => {
    this.editingMessage = message;
    this.showEditModal = true;
  };

  closeEditModal = () => {
    this.editingMessage = null;
    this.showEditModal = false;
  };

  saveEdit = async (message: Message) => {
    await deepSave(message, this.store, { maxDepth: 1 });
    this.showEditModal = false;
    this.reload();
  };

  // Delete
  deleteThread = async (thread: Message) => {
    if (
      confirm('Are you sure you want to delete this thread and all replies?')
    ) {
      await thread.destroyRecord();
      this.reload();
    }
  };

  deleteReply = async (reply: Message) => {
    if (confirm('Are you sure you want to delete this reply?')) {
      await reply.destroyRecord();
      this.reload();
    }
  };

  // Permission check
  canEdit = (userId: string): boolean => {
    if (!this.session.cached.userId) {
      return false;
    }
    return userId === this.session.cached.userId;
  };

  <template>
    {{pageTitle "Discussions"}}
    <ContentBreadcrumb @model={{@model}} />

    <div class="mb-5">
      <EditContext as |context|>
        <Sidebar>
          <:content>
            <ProgramNavTemplate
              @program={{@model.program}}
              @cohort={{@model.cohort}}
              @parts={{parts}}
            />
            <CohortNavTemplate
              @program={{@model.program}}
              @cohort={{@model.cohort}}
              @parts={{parts}}
            >
              <div class="container py-4">
                <h3 class="gud-eyebrow mb-4">Discussion Threads</h3>

                <Request @query={{this.query}}>
                  <:content as |result|>
                    {{#let (filterThreads result.data) as |threads|}}
                      {{#if threads.length}}
                        <div class="ll-discussions">
                          {{#each threads as |thread|}}
                            {{#let
                              (getRepliesForThread result.data thread.id)
                              as |replies|
                            }}
                              <div class="ll-thread card mb-3">
                                <div class="card-body">
                                  {{! Thread header }}
                                  <div
                                    class="d-flex justify-content-between align-items-start mb-2"
                                  >
                                    <h5 class="card-title mb-0">
                                      {{thread.title}}
                                    </h5>
                                    <IfAbility
                                      @ability="messages"
                                      @atLeast="editor"
                                    >
                                      <:if>
                                        <button
                                          type="button"
                                          class="btn btn-sm btn-outline-danger"
                                          {{on
                                            "click"
                                            (fn this.deleteThread thread)
                                          }}
                                        >
                                          <iconify-icon
                                            inline
                                            icon="ph:trash"
                                          />
                                        </button>
                                      </:if>
                                    </IfAbility>
                                  </div>

                                  {{! Thread content }}
                                  <MessagePost
                                    @message={{thread}}
                                    @unsafe={{true}}
                                  >
                                    <:actions>
                                      <PostActions
                                        @allowReply={{true}}
                                        @allowEdit={{this.canEdit thread.user}}
                                        @allowDelete={{false}}
                                        @onReply={{fn
                                          this.openReplyModal
                                          thread
                                        }}
                                        @onEdit={{fn this.openEditModal thread}}
                                      />
                                    </:actions>
                                  </MessagePost>

                                  {{! Replies toggle and inline replies }}
                                  {{#if replies.length}}
                                    <div class="mt-3">
                                      <button
                                        type="button"
                                        class="btn btn-sm btn-link text-muted p-0"
                                        {{on
                                          "click"
                                          (fn this.toggleThread thread.id)
                                        }}
                                      >
                                        <iconify-icon
                                          inline
                                          icon={{if
                                            (this.isExpanded thread.id)
                                            "ph:caret-down"
                                            "ph:caret-right"
                                          }}
                                        />
                                        {{replies.length}}
                                        {{if
                                          (isPlural replies.length)
                                          "replies"
                                          "reply"
                                        }}
                                      </button>

                                      {{#if (this.isExpanded thread.id)}}
                                        <div
                                          class="ll-thread-replies ms-4 mt-3 border-start ps-3"
                                        >
                                          {{#each replies as |reply|}}
                                            <MessagePost
                                              @message={{reply}}
                                              @unsafe={{true}}
                                              class="ll-thread-reply mb-3"
                                            >
                                              <:actions>
                                                <PostActions
                                                  @allowEdit={{this.canEdit
                                                    reply.user
                                                  }}
                                                  @allowDelete={{true}}
                                                  @onEdit={{fn
                                                    this.openEditModal
                                                    reply
                                                  }}
                                                  @onDelete={{fn
                                                    this.deleteReply
                                                    reply
                                                  }}
                                                />
                                              </:actions>
                                            </MessagePost>
                                          {{/each}}
                                        </div>
                                      {{/if}}
                                    </div>
                                  {{/if}}
                                </div>
                              </div>
                            {{/let}}
                          {{/each}}
                        </div>
                      {{else}}
                        <div class="alert alert-info">
                          <iconify-icon inline icon="ph:chats-circle" />
                          No discussion threads have been started yet.
                        </div>
                      {{/if}}
                    {{/let}}
                  </:content>
                  <:loading>
                    <div class="d-flex justify-content-center py-5">
                      <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  </:loading>
                  <:error as |error|>
                    <div class="alert alert-danger">
                      <iconify-icon inline icon="ph:warning" />
                      Failed to load discussions:
                      {{error.message}}
                    </div>
                  </:error>
                </Request>
              </div>
            </CohortNavTemplate>
          </:content>
          <:sidebar>
            <div class="px-3 py-4">
              <IfAbility @ability="messages" @atLeast="editor">
                <:if>
                  <div class="mb-5">
                    <h4 class="gud-eyebrow">Actions</h4>
                    <button
                      type="button"
                      class="btn btn-primary w-100"
                      {{on "click" this.openNewThreadModal}}
                    >
                      <iconify-icon inline icon="ph:plus" />
                      New Thread
                    </button>
                  </div>
                </:if>
              </IfAbility>

              <EditCohortVisibility
                @item={{@model.cohort}}
                @context={{context}}
                class="mb-5"
              />
            </div>
          </:sidebar>
        </Sidebar>
      </EditContext>
    </div>

    {{! New Thread Modal }}
    <BsModal
      @open={{this.showNewThreadModal}}
      @onHidden={{this.closeNewThreadModal}}
      @backdropClose={{false}}
      @renderInPlace={{false}}
      @size="lg"
      as |modal|
    >
      <modal.header>
        <h4 class="modal-title">Start a New Discussion</h4>
      </modal.header>
      <modal.body>
        {{#if this.newThread}}
          <PostEditor
            @message={{this.newThread}}
            @onSave={{this.saveThread}}
            @onCancel={{this.closeNewThreadModal}}
            @allowSchedule={{true}}
            @saveLabel="Start Thread"
          />
        {{/if}}
      </modal.body>
    </BsModal>

    {{! Reply Modal }}
    <BsModal
      @open={{this.showReplyModal}}
      @onHidden={{this.closeReplyModal}}
      @backdropClose={{false}}
      @renderInPlace={{false}}
      @size="lg"
      as |modal|
    >
      <modal.header>
        <h4 class="modal-title">
          Reply to:
          {{this.replyingToThread.title}}
        </h4>
      </modal.header>
      <modal.body>
        {{#if this.newReply}}
          <PostEditor
            @message={{this.newReply}}
            @onSave={{this.saveReply}}
            @onCancel={{this.closeReplyModal}}
            @isReply={{true}}
            @allowSchedule={{true}}
            @saveLabel="Post Reply"
          />
        {{/if}}
      </modal.body>
    </BsModal>

    {{! Edit Modal }}
    <BsModal
      @open={{this.showEditModal}}
      @onHidden={{this.closeEditModal}}
      @backdropClose={{false}}
      @renderInPlace={{false}}
      @size="lg"
      as |modal|
    >
      <modal.header>
        <h4 class="modal-title">Edit Post</h4>
      </modal.header>
      <modal.body>
        {{#if this.editingMessage}}
          <PostEditor
            @message={{this.editingMessage}}
            @onSave={{this.saveEdit}}
            @onCancel={{this.closeEditModal}}
            @isReply={{not (not this.editingMessage.parent)}}
            @allowSchedule={{true}}
            @saveLabel="Update"
          />
        {{/if}}
      </modal.body>
    </BsModal>
  </template>
}

function not(value: unknown): boolean {
  return !value;
}
